import isImportFileSelected from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Sidebars/ImportDocumentSidebar/FormData/constraint/isImportFileSelected';
import isImportFileSizeAccepted from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Sidebars/ImportDocumentSidebar/FormData/constraint/isImportFileSizeAccepted';
import isImportFileTypeAccepted from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Sidebars/ImportDocumentSidebar/FormData/constraint/isImportFileTypeAccepted';
import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';

export type ImportDocumentFormArgs = {
	name: string;
	description: string;
	file: File | null;
};

/**
 * `ignoreNullValue` is deliberately absent: with it, `file: null` would count as valid and its
 * constraints would never run.
 */
@ValidatedClass()
class ImportDocumentFormData extends AbstractValidatedClass<ImportDocumentFormArgs> {
	@constraint(Joi.string().trim().max(180), () => '180 caractères maximum')
	@constraint(Joi.string().trim().min(1), () => 'Le nom est obligatoire')
	public readonly name: string;

	@constraint(Joi.string().max(2000).allow(''), () => '2000 caractères maximum')
	public readonly description: string;

	@constraint(isImportFileSizeAccepted, () => 'Le fichier dépasse 20 Mo')
	@constraint(isImportFileTypeAccepted, () => 'Le fichier doit être un document Word (.docx) ou PDF (.pdf)')
	@constraint(isImportFileSelected, () => 'Choisissez un fichier Word ou PDF')
	public readonly file: File | null;

	constructor(data: ImportDocumentFormArgs) {
		super(data);
		this.name = data.name;
		this.description = data.description;
		this.file = data.file;
		Object.freeze(this);
	}

	static createEmpty(): ImportDocumentFormData {
		return new ImportDocumentFormData({ name: '', description: '', file: null });
	}

	setName(name: string): this {
		return this.cloneWith({ name } as Partial<ImportDocumentFormArgs>);
	}

	setDescription(description: string): this {
		return this.cloneWith({ description } as Partial<ImportDocumentFormArgs>);
	}

	/** Prefills an empty `name` with the file name minus its extension. */
	setFile(file: File | null): this {
		const baseName = file === null ? '' : file.name.replace(/\.[^.]+$/, '');
		const name = this.name.trim() === '' ? baseName : this.name;
		return this.cloneWith({ file, name } as Partial<ImportDocumentFormArgs>);
	}
}

export default ImportDocumentFormData;
